import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Modal, Form, Input, Select, Space, DatePicker, message, Card, ColorPicker } from 'antd';
import { collectDirectServerIds } from '../utils/projectDirectServers';
import { EditOutlined, DeleteOutlined, PlusOutlined, ProjectOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useListProjectsQuery, useAddProjectMutation, useEditProjectMutation, useDeleteProjectMutation, useListClientsQuery } from '../store/api';

const { Option } = Select;

const Proyectos = () => {
  const navigate = useNavigate();
  const { data = [], isLoading } = useListProjectsQuery();
  const { data: clients = [] } = useListClientsQuery();
  const [addProject] = useAddProjectMutation();
  const [editProject] = useEditProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');

  const filtered = data.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const blob = [p.nombre, p.descripcion, p.url_produccion, p.tech_stack, p.estado, p.como_funciona]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return blob.includes(q) || String(p.id) === q;
  });

  const showModal = (record = null) => {
    setEditingRecord(record);
    record ? form.setFieldsValue({ ...record, fecha_inicio: record.fecha_inicio ? dayjs(record.fecha_inicio) : null, color: record.color || '#845adf' }) : form.resetFields();
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const colorVal = typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || '#845adf';
      const fv = { ...values, color: colorVal, fecha_inicio: values.fecha_inicio?.format('YYYY-MM-DD') || null };
      if (editingRecord?.id) {
        await editProject({ id: editingRecord.id, ...fv }).unwrap();
        message.success('Proyecto actualizado');
      } else {
        await addProject(fv).unwrap();
        message.success('Proyecto creado');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      message.error('Error al guardar proyecto');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '¿Eliminar proyecto?', content: 'Esta acción no se puede deshacer.',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProject(id).unwrap();
          message.success('Proyecto eliminado');
        } catch { message.error('Error al eliminar'); }
      },
    });
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.nombre || id;

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Proyecto', dataIndex: 'nombre', key: 'nombre', render: (t, r) => (
      <span style={{ fontWeight: 600, cursor: 'pointer', color: '#845adf' }} onClick={() => navigate(`/proyecto/${r.id}`)}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.color || '#845adf', marginRight: 6 }} />
        {t}
      </span>
    )},
    { title: 'Cliente', dataIndex: 'cliente_id', key: 'cliente_id', render: id => getClientName(id) },
    { title: 'Función', dataIndex: 'descripcion', key: 'descripcion', ellipsis: true, width: 200 },
    { title: 'Srv.', key: 'srv_count', width: 56, align: 'center',
      render: (_, r) => collectDirectServerIds(r).length },
    { title: 'URL', dataIndex: 'url_produccion', key: 'url', width: 150, ellipsis: true,
      render: u => u ? <a href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, '')}</a> : '-' },
    { title: 'URL administrativa', dataIndex: 'url_admin', key: 'url_admin', width: 160, ellipsis: true,
      render: u => u ? <a href={u} target="_blank" rel="noreferrer">{u.replace(/^https?:\/\//, '')}</a> : '-' },
    { title: 'Credenciales', dataIndex: 'credenciales_acceso', key: 'credenciales', width: 140, ellipsis: true,
      render: t => t ? <span title={t}>{String(t).split('\n')[0]}</span> : '-' },
    { title: 'Tech Stack', dataIndex: 'tech_stack', key: 'tech_stack', render: t => t ? <Tag color="purple">{t}</Tag> : '-' },
    { title: 'Fecha Inicio', dataIndex: 'fecha_inicio', key: 'fecha_inicio' },
    { title: 'Estado', dataIndex: 'estado', key: 'estado', render: s => {
        const color = s === 'Completado' ? 'green' : s === 'Pendiente' ? 'orange' : 'geekblue';
        return <Tag color={color} style={{ borderRadius: 12 }}>{s?.toUpperCase() || 'N/A'}</Tag>;
    }},
    { title: 'Acciones', key: 'acciones', render: (_, record) => (
        <Space size={4}>
          <button className="action-btn" title="Ver Detalle" onClick={() => navigate(`/proyecto/${record.id}`)}><EyeOutlined /></button>
          <button className="action-btn" title="Editar" onClick={() => showModal(record)}><EditOutlined /></button>
          <button className="action-btn" title="Eliminar" onClick={() => handleDelete(record.id)} style={{ color: '#f43f5e' }}><DeleteOutlined /></button>
        </Space>
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><ProjectOutlined style={{ marginRight: 8 }} />Proyectos</h1>
          <div className="page-subtitle">{filtered.length} de {data.length} proyectos</div>
        </div>
        <Space>
          <Input.Search
            allowClear
            placeholder="Buscar por nombre, URL, stack, ID…"
            style={{ width: 280 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal(null)}
          style={{ borderRadius: 8, fontWeight: 600, background: 'linear-gradient(135deg, #845adf, #6c3fc5)', border: 'none' }}>
          Nuevo Proyecto
        </Button>
        </Space>
      </div>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <div className="modern-table">
          <Table columns={columns} dataSource={filtered} rowKey="id" loading={isLoading}
            pagination={{ pageSize: 25, showTotal: t => `${t} proyectos` }} scroll={{ x: true }} size="middle" />
        </div>
      </Card>

      <Modal title={editingRecord ? 'Editar Proyecto' : 'Nuevo Proyecto'} open={isModalVisible}
        onOk={handleSave} onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
        okText="Guardar" cancelText="Cancelar">
        <Form form={form} layout="vertical">
          <Form.Item name="nombre" label="Nombre del Proyecto" rules={[{ required: true, message: 'Ingrese el nombre' }]}><Input /></Form.Item>
          <Form.Item name="descripcion" label="Función del sitio (breve)"><Input.TextArea rows={2} placeholder="Qué hace este proyecto..." /></Form.Item>
          <Form.Item name="como_funciona" label="Cómo funciona (detalle, Markdown)">
            <Input.TextArea rows={5} placeholder="Flujo para el usuario, APIs, BD y servidores..." />
          </Form.Item>
          <Form.Item name="cliente_id" label="Cliente" rules={[{ required: true, message: 'Seleccione un cliente' }]}>
            <Select>{clients.map(c => <Option key={c.id} value={c.id}>{c.nombre}</Option>)}</Select>
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="fecha_inicio" label="Fecha de Inicio"><DatePicker style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="estado" label="Estado" initialValue="Pendiente">
              <Select>
                <Option value="Pendiente">Pendiente</Option>
                <Option value="En Progreso">En Progreso</Option>
                <Option value="Completado">Completado</Option>
                <Option value="Cancelado">Cancelado</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="tech_stack" label="Tech Stack"><Input placeholder="React, Node.js, MySQL..." /></Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="url_produccion" label="URL (producción)"><Input placeholder="https://..." /></Form.Item>
            <Form.Item name="url_admin" label="URL administrativa"><Input placeholder="https://.../admin" /></Form.Item>
          </div>
          <Form.Item name="url_staging" label="URL Staging"><Input placeholder="https://..." /></Form.Item>
          <Form.Item name="credenciales_acceso" label="Credenciales">
            <Input.TextArea rows={3} placeholder="Usuario, contraseña y notas de acceso" />
          </Form.Item>
          <Form.Item name="manual_agente" label="Manual de agente (ruta o URL)">
            <Input placeholder="G:\...\AGENTS.md o https://..." />
          </Form.Item>
          <Form.Item name="repositorio" label="Repositorio"><Input placeholder="https://github.com/..." /></Form.Item>
          <Form.Item name="color" label="Color" initialValue="#845adf"><ColorPicker /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Proyectos;
